import React, { useState } from 'react';

import { Box } from '@mui/material';

import { useGetRoomRateBlackoutDates } from '~/hooks/apis/reservation/useGetRoomRateBlackoutDates';
import { useGetRoomRateSurchargeDates } from '~/hooks/apis/reservation/useGetRoomRateSurchargeDates';
import { useGetVendor } from '~/hooks/apis/vendors/useGetVendor';

import BlackoutsTab from './BlackoutsTab';
import CapacitiesTab from './CapacitiesTab';
import ExtraGuestSurchargesTab from './ExtraGuestSurchargesTab';
import IncludedGuestsTab from './IncludedGuestsTab';
import InventorySubTabs, { type InventorySubTab } from './InventorySubTabs';
import InventoryTab from './InventoryTab';
import LeadDaysTab from './LeadDaysTab';
import LengthsOfStayTab from './LengthsOfStayTab';
import RevenueManagementTab from './RevenueManagementTab';
import SurchargesTab from './SurchargesTab';

interface Props {
  propertyId: string;
  vendorId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
  propertyRoomTypes: Array<App.RoomType>;
  channelManaged: boolean;
}

export default function RoomRateSubTabsPanel({
  propertyId,
  vendorId,
  roomType,
  roomRate,
  propertyRoomTypes,
  channelManaged,
}: Props) {
  const [subTab, setSubTab] = useState<InventorySubTab>('Inventory');
  const { data: vendor } = useGetVendor(vendorId);
  const { data: surchargeDates = [] } = useGetRoomRateSurchargeDates(propertyId, roomType.id, roomRate.id);
  const { data: blackoutDates = [] } = useGetRoomRateBlackoutDates(propertyId, roomType.id, roomRate.id);

  return (
    <Box sx={{ mt: 3 }}>
      <InventorySubTabs
        value={subTab}
        onChange={setSubTab}
        tabCounts={{ Surcharges: surchargeDates.length, Blackouts: blackoutDates.length }}
      />

      {subTab === 'Inventory' && (
        <InventoryTab
          propertyId={propertyId}
          vendorId={vendorId}
          roomType={roomType}
          roomRate={roomRate}
          propertyRoomTypes={propertyRoomTypes}
          channelManaged={channelManaged}
        />
      )}

      {subTab === 'Surcharges' && (
        <SurchargesTab
          propertyId={propertyId}
          roomType={roomType}
          roomRate={roomRate}
          propertyRoomTypes={propertyRoomTypes}
          vendorCurrencyCode={vendor?.currencyisocode}
        />
      )}

      {subTab === 'Blackouts' && (
        <BlackoutsTab
          propertyId={propertyId}
          roomType={roomType}
          roomRate={roomRate}
          propertyRoomTypes={propertyRoomTypes}
          channelManaged={channelManaged}
        />
      )}

      {subTab === 'Lead days' && <LeadDaysTab propertyId={propertyId} roomType={roomType} roomRate={roomRate} />}

      {subTab === 'Lengths of stay' && (
        <LengthsOfStayTab propertyId={propertyId} roomType={roomType} roomRate={roomRate} />
      )}

      {subTab === 'Revenue management' && (
        <RevenueManagementTab
          propertyId={propertyId}
          vendorId={vendorId}
          roomType={roomType}
          roomRate={roomRate}
          propertyRoomTypes={propertyRoomTypes}
          vendorCurrencyCode={vendor?.currencyisocode}
        />
      )}

      {subTab === 'Extra guest surcharge' && (
        <ExtraGuestSurchargesTab
          propertyId={propertyId}
          roomType={roomType}
          roomRate={roomRate}
          vendorCurrencyCode={vendor?.currencyisocode}
        />
      )}

      {subTab === 'Capacity' && <CapacitiesTab propertyId={propertyId} roomType={roomType} roomRate={roomRate} />}

      {subTab === 'Included guests' && (
        <IncludedGuestsTab propertyId={propertyId} roomType={roomType} roomRate={roomRate} />
      )}
    </Box>
  );
}
